/**
 * Permission codes the Treasury screens declare an interest in.
 *
 * They are the dot-strings `internal/treasury/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once
 * here means a renamed permission is a compile error rather than a menu that
 * silently disappears.
 *
 * **Four of these are MFA step-up guarded server-side** — `beneficiary.approve`,
 * `beneficiary.release_early`, `beneficiary.reveal`, `cash.transfer.release`,
 * `treasury.debt.post` and the two bank-account cooling-off codes. The screens
 * therefore treat `ERR_STEP_UP_REQUIRED` as a guided step rather than a refusal;
 * `@/api/client` answers the challenge and replays the request, so no screen
 * here contains a word about it.
 *
 * **`treasury.wallet.read` and `treasury.wallet.capture` are held by
 * `RoleEmployee`** and are row-scoped server-side (§6.10): a custodian reaches
 * their own float and a stranger gets 404, never 403. Holding the permission is
 * therefore not the same as being able to see a particular wallet, and no screen
 * here treats it as though it were.
 */

export const TREASURY_PERMISSIONS = {
  settingsRead: 'treasury.settings.read',
  settingsManage: 'treasury.settings.manage',

  observationRead: 'treasury.observation.read',
  observationRecord: 'treasury.observation.record',

  positionRead: 'treasury.position.read',
  positionPublish: 'treasury.position.publish',
  positionVerify: 'treasury.position.verify',
  /**
   * The consolidated group position across legal entities (FSD 3.2.1 S05 V052; FT9-B16). Its own
   * permission, not an implication of `positionRead`. FT11-U37 declares the same string locally as
   * `POSITION_GROUP_READ` in `positions/api.ts`; repoint that constant here when the module is next touched.
   */
  positionGroupRead: 'treasury.position.group.read',

  beneficiaryRead: 'treasury.beneficiary.read',
  beneficiaryManage: 'treasury.beneficiary.manage',
  beneficiaryApprove: 'treasury.beneficiary.approve',
  beneficiaryReleaseEarly: 'treasury.beneficiary.release_early',
  beneficiaryVerify: 'treasury.beneficiary.verify',
  beneficiaryReveal: 'treasury.beneficiary.reveal',

  mandateRead: 'treasury.mandate.read',
  mandateManage: 'treasury.mandate.manage',

  bankAccountApprove: 'treasury.bank_account.approve',
  bankAccountReleaseEarly: 'treasury.bank_account.release_early',
  bankAccountManage: 'treasury.bank_account.manage',

  walletRead: 'treasury.wallet.read',
  walletManage: 'treasury.wallet.manage',
  walletCapture: 'treasury.wallet.capture',
  walletApprove: 'treasury.wallet.approve',
  walletCount: 'treasury.wallet.count',
  walletCountPost: 'treasury.wallet.count.post',
  walletCustody: 'treasury.wallet.custody',
  /** Acknowledge a custody handover as the outgoing or incoming custodian (V068). */
  walletHandover: 'treasury.wallet.handover',
  /** Unmask a wallet's custodian and deputy (R053). MFA step-up; audited. */
  walletReveal: 'treasury.wallet.reveal',
  /** Recover a wallet's balance to its funding account (R028). MFA step-up. */
  walletRecover: 'treasury.wallet.recover',
  walletReplenish: 'treasury.wallet.replenish',

  debtRead: 'treasury.debt.read',
  debtManage: 'treasury.debt.manage',
  debtPost: 'treasury.debt.post',
  /** V105's second principal: activate/reject a facility, decide a change, approve a movement. MFA step-up. */
  debtApprove: 'treasury.debt.approve',
  /** Request, complete or withdraw the dual-authorised close of a facility (V110). */
  debtClose: 'treasury.debt.close',
  covenantTest: 'treasury.covenant.test',

  forecastRead: 'treasury.forecast.read',
  forecastPublish: 'treasury.forecast.publish',
  /** Record a manual override of one forecast line, with a reason and an expiry (V088). */
  forecastOverride: 'treasury.forecast.override',
  scenarioManage: 'treasury.scenario.manage',

  /** The four `internal/cash` codes the instruction lifecycle adds, plus create. */
  transferRead: 'cash.transfer.read',
  transferCreate: 'cash.transfer.create',
  transferSubmit: 'cash.transfer.submit',
  transferApprove: 'cash.transfer.approve',
  transferSign: 'cash.transfer.sign',
  transferRelease: 'cash.transfer.release',
  transferConfirm: 'cash.transfer.confirm',

  /** Read companies, accounts and bank accounts to scope every screen here. */
  companyRead: 'org.company.read',
  glAccountRead: 'org.account.read',
  bankAccountRead: 'cash.account.read',
  /** Pledging a bank account as debt security sets its liquidity class (V112). */
  bankAccountUpdate: 'cash.account.update',
  /** The intercompany-instrument picker an intercompany loan is registered with (V106). */
  icInstrumentRead: 'cash.ic_instrument.read',
  /** The statement-line picker a drawdown or repayment is evidenced with (V107). */
  statementRead: 'bankrec.statement.read',
  /** Drill from a published figure into the postings behind it. */
  glReportRead: 'gl.report.read',
  journalRead: 'gl.journal.read',
} as const

export type TreasuryPermission =
  (typeof TREASURY_PERMISSIONS)[keyof typeof TREASURY_PERMISSIONS]
